// import Link from '@/components/link-component'
import PageWrapper from '@/components/page-wrapper'
import SocialShare from '@/components/social-share'
import RichText from '@/components/ui/rich-text'
import ArticleHeader from '@/components/article-header'
import ArticleBreadcrumb from '@/components/article-breadcrumb'
import Code from '@/components/ui/code/code'
import Image from '@/components/ui/image'
import Video from '@/components/ui/video'
import TocNav from '@/components/toc-nav'
import Link from '@/components/link-component'
import SectionCta from '@/components/section/cta'

import Table from '@/components/table'
import { slugify } from '@/utils/strings'

import { TypeBlogPage, TypeSectionCta } from '@/types/ctf'
import Lottie from '@/components/lottie'
import { localizedPath, useLocale } from '@/utils/locale'
import { usePage } from '@inertiajs/react'

type BlogPageProps = {
  post: TypeBlogPage<'WITHOUT_UNRESOLVABLE_LINKS'>
  defaultCta: TypeSectionCta<'WITHOUT_UNRESOLVABLE_LINKS'>
  headerNav: any
  footerNav: any
  headerBanners: any
  newsletterForm: any
  seo: any
}

export default function BlogPage({
  post: { fields },
  defaultCta,
  headerNav,
  footerNav,
  seo,
  headerBanners,
  newsletterForm,
}: BlogPageProps) {
  const page = usePage()
  const { currentLocale } = useLocale(page.url)
  const {
    title,
    slug,
    author,
    description,
    publishDate,
    heroImageGradientOverlay,
    heroImage,
    heroBackgroundColor = 'Midnight',
    ogImage,
    content,
    cta,
    tags,
  } = fields
  const breadcrumb = [{ name: 'Blog', path: '/blog/' }, { name: title }]
  const outlineBody = content
    .filter((item) => item?.sys.contentType.sys.id === 'componentRichText')
    .map((item) => {
      return { ...item?.fields, __typename: 'ContentfulComponentRichText' }
    })
  const pageSeo = {
    fields: {
      title,
      description,
      ogImage: ogImage || heroImage,
    },
  }

  return (
    <PageWrapper
      bodyTw="bg-secondary-50 dark:bg-secondary-900"
      {...({
        seo: { ...seo, ...pageSeo },
        breadcrumb,
        headerNav,
        footerNav,
        headerBanners,
        newsletterForm,
      } as any)}
    >
      <>
        <ArticleHeader
          author={author}
          image={heroImage}
          imageOverlay={heroImageGradientOverlay}
          tagline="The Bitwarden Blog"
          publishDate={publishDate}
          title={title}
          backgroundColor={heroBackgroundColor}
        />
        <article className="mx-auto max-w-8xl md:px-5 lg:px-9">
          <div className="lg:flex space-x-10 py-10 px-5 xl:px-0">
            <div className="max-w-[740px]">
              <ArticleBreadcrumb breadcrumb={breadcrumb} />
              {content?.map((item: any) => {
                const {
                  sys: { contentType },
                } = item
                if (contentType.sys.id === 'componentRichText')
                  return (
                    <section key={item.sys.id}>
                      <RichText body={item.fields.body} />
                    </section>
                  )
                if (contentType.sys.id === 'sectionCta') {
                  return <SectionCta key={item.sys.id} {...item.fields} className="!px-0 mb-10" />
                }
                if (contentType.sys.id === 'componentCode') {
                  return <Code key={item.sys.id} body={item.fields.body} language={item.fields.language} />
                }
                if (contentType.sys.id === 'resourceImage') {
                  return <Image key={item.sys.id} {...item.fields} />
                }
                if (contentType.sys.id === 'googlePresentation')
                  return (
                    <div key={item.sys.id} className="relative aspect-video">
                      <iframe
                        title={title}
                        src={item.fields.url}
                        width="100%"
                        height="100%"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                        }}
                        sandbox="allow-scripts allow-popups"
                      />
                    </div>
                  )
                if (contentType.sys.id === 'componentVideo')
                  return (
                    <div key={item.sys.id} className="my-10">
                      <Video {...item} />
                    </div>
                  )

                if (contentType.sys.id === 'componentTable')
                  return (
                    <div key={item.sys.id} className="my-10">
                      <Table {...item.fields} />
                    </div>
                  )

                if (contentType.sys.id === 'componentLottie')
                  return <Lottie key={item.sys.id} {...item.fields} className="my-10" />

                return null
              })}

              {tags && tags.length > 0 && (
                <div className="flex flex-wrap pt-10 items-center gap-2">
                  <span className="font-bwi mr-2">&#xe95e;</span>
                  {tags.map((tag) => (
                    <Link
                      className="rounded-full py-1 px-6 bg-secondary-100 font-bold dark:text-secondary-50"
                      key={tag}
                      to={localizedPath(`/blog/tag/${slugify(tag)}/`, currentLocale)}
                    >
                      {tag}
                    </Link>
                  ))}
                </div>
              )}

              <div className="md:flex flex-row-reverse items-center justify-between lg:block my-10">
                <div className="block lg:hidden mb-8 md:mb-0">
                  <SocialShare title={title} slug={`/blog/${slug}`} />
                </div>
                <Link to="/blog/">
                  <span className="font-bwi mr-4">&#xe96b;</span>
                  {'Back to Blog'}
                </Link>
              </div>
            </div>

            <div className="hidden lg:block flex-grow">
              <div className="sticky top-24 overflow-y-auto p-8 bg-white rounded-lg border-b-2 border-secondary-100 dark:bg-secondary-800">
                <SocialShare title={title} slug={`/blog/${slug}`} />
                <div className="max-h-[calc(100vh-8rem)] overflow-y-auto">
                  <TocNav id="blog-page-nav" sections={outlineBody as never[]} scrollToOffset={100} />
                </div>
              </div>
            </div>
          </div>
        </article>

        <div className="pb-20">
          {cta ? <SectionCta {...(cta.fields as any)} /> : <SectionCta {...defaultCta.fields} />}
        </div>
      </>
    </PageWrapper>
  )
}
